import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, getErrorMessage } from '../../lib/api';
import { toast, alert } from '../../utils/toast';
import {
  Webhook as WebhookIcon,
  Plus,
  Send,
  Trash2,
  Eye,
  EyeOff,
  Copy,
  Check,
  PauseCircle,
  PlayCircle,
  ShieldCheck,
  ChevronDown,
  ChevronRight,
  History,
  RotateCw,
  XCircle,
  CheckCircle2,
  Clock,
  X,
  Code,
} from 'lucide-react';

export interface WebhookDelivery {
  id: string;
  developer_webhook_id: string;
  event_type: string;
  payload: Record<string, unknown>;
  status: 'pending' | 'success' | 'failed';
  attempts: number;
  last_attempt_at: string | null;
  response_status: number | null;
  response_body: string | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

interface Webhook {
  id: string;
  application_id: string;
  webhook_url: string;
  events: string[];
  enabled: boolean;
  secret_key?: string;
  last_delivery_at: string | null;
  last_delivery_status: string | null;
  failed_deliveries: number;
}

interface WebhookManagerProps {
  clientId: string;
  applicationId: string;
}

const AVAILABLE_EVENTS = [
  {
    id: 'subscription.activated',
    name: 'Subscrição Ativada',
    desc: 'Ativação de plano ou confirmação de pagamento de subscrição.',
  },
  {
    id: 'subscription.suspended',
    name: 'Subscrição Suspensa',
    desc: 'Cancelamento, expiração ou esgotamento de quota.',
  },
  {
    id: 'transaction.charged',
    name: 'Transação Cobrada',
    desc: 'Cobrança de pedidos ou débito de saldo na carteira.',
  },
  {
    id: 'endpoint.updated',
    name: 'Endpoint Modificado',
    desc: 'Modificações de esquema ou publicação de novas rotas.',
  },
];

export const WebhookManager: React.FC<WebhookManagerProps> = ({ clientId, applicationId }) => {
  const queryClient = useQueryClient();
  const [isAdding, setIsAdding] = useState(false);
  const [newUrl, setNewUrl] = useState('');
  const [selectedEvents, setSelectedEvents] = useState<string[]>([
    'subscription.activated',
    'subscription.suspended',
    'transaction.charged',
  ]);
  const [visibleSecrets, setVisibleSecrets] = useState<Set<string>>(new Set());
  const [copiedSecretId, setCopiedSecretId] = useState<string | null>(null);
  const [showVerifyGuide, setShowVerifyGuide] = useState(false);
  const [guideLang, setGuideLang] = useState<'node' | 'python' | 'php'>('node');
  const [openDeliveries, setOpenDeliveries] = useState<Set<string>>(new Set());

  // Fetch Webhooks
  const { data: webhooks = [], isLoading } = useQuery({
    queryKey: ['webhooks', clientId, applicationId],
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: Webhook[] }>(
        `/clients/${clientId}/applications/${applicationId}/webhooks`
      );
      return response.data.data;
    },
  });

  // Create Webhook Mutation
  const createMutation = useMutation({
    mutationFn: async ({ url, events }: { url: string; events: string[] }) => {
      const payload = {
        webhook_url: url,
        events: events.length > 0 ? events : ['subscription.activated'],
        enabled: true,
      };
      const response = await api.post<{ success: boolean; data: Webhook }>(
        `/clients/${clientId}/applications/${applicationId}/webhooks`,
        payload
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks', clientId, applicationId] });
      setIsAdding(false);
      setNewUrl('');
      setSelectedEvents([
        'subscription.activated',
        'subscription.suspended',
        'transaction.charged',
      ]);
      toast.success('Endpoint de Webhook adicionado com sucesso!');
    },
    onError: (err) => {
      toast.error(`Falha ao adicionar Webhook: ${getErrorMessage(err)}`);
    },
  });

  // Toggle Webhook Enabled/Disabled Mutation
  const toggleMutation = useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) => {
      const response = await api.put<{ success: boolean; data: Webhook }>(
        `/clients/${clientId}/applications/${applicationId}/webhooks/${id}`,
        { enabled }
      );
      return response.data.data;
    },
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['webhooks', clientId, applicationId] });
      toast.success(
        updated.enabled ? 'Webhook ativado com sucesso!' : 'Webhook pausado com sucesso!'
      );
    },
    onError: (err) => {
      toast.error(`Falha ao alterar estado do webhook: ${getErrorMessage(err)}`);
    },
  });

  // Delete Webhook Mutation
  const deleteMutation = useMutation({
    mutationFn: async (webhookId: string) => {
      await api.delete(`/clients/${clientId}/applications/${applicationId}/webhooks/${webhookId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks', clientId, applicationId] });
      toast.success('Webhook eliminado com sucesso!');
    },
    onError: (err) => {
      toast.error(`Falha ao eliminar Webhook: ${getErrorMessage(err)}`);
    },
  });

  // Test Ping Mutation
  const testMutation = useMutation({
    mutationFn: async (webhookId: string) => {
      const response = await api.post<{ success: boolean; message: string }>(
        `/clients/${clientId}/applications/${applicationId}/webhooks/${webhookId}/test`
      );
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['webhooks', clientId, applicationId] });
      toast.success(data?.message || 'Ping de teste enviado com sucesso!');
    },
    onError: (err) => {
      toast.error(`Falha no ping de teste: ${getErrorMessage(err)}`);
    },
  });

  const toggleSecretVisibility = (id: string) => {
    const next = new Set(visibleSecrets);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setVisibleSecrets(next);
  };

  const handleCopySecret = async (secret: string, id: string) => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopiedSecretId(id);
      toast.success('Chave secreta copiada para a área de transferência!');
      setTimeout(() => setCopiedSecretId(null), 2000);
    } catch {
      toast.error('Erro ao copiar chave secreta.');
    }
  };

  const toggleEvent = (eventId: string) => {
    setSelectedEvents((prev) =>
      prev.includes(eventId) ? prev.filter((e) => e !== eventId) : [...prev, eventId]
    );
  };

  const handleAddSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUrl) return;
    createMutation.mutate({ url: newUrl, events: selectedEvents });
  };

  if (isLoading) {
    return (
      <div className="p-4 text-center text-slate-500 dark:text-slate-400 text-sm">
        A carregar webhooks...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h3 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <WebhookIcon className="w-5 h-5 text-primary-500" />
            <span>Webhooks do Desenvolvedor</span>
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Receba notificações assíncronas (callbacks HTTP) quando eventos importantes ocorrerem.
          </p>
        </div>
        {!isAdding && (
          <button
            onClick={() => setIsAdding(true)}
            className="btn-base btn-primary btn-sm text-xs px-4 py-2 flex items-center gap-1.5 self-start sm:self-auto"
          >
            <Plus className="w-4 h-4" /> Adicionar Endpoint
          </button>
        )}
      </div>

      {/* Expandable HMAC-SHA256 Signature Verification Guide */}
      <div className="bg-slate-50 dark:bg-slate-800/40 rounded-2xl border border-slate-200/80 dark:border-slate-700/80 overflow-hidden shadow-xs">
        <button
          type="button"
          onClick={() => setShowVerifyGuide((prev) => !prev)}
          className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-100/60 dark:hover:bg-slate-800 transition-colors"
        >
          <div className="flex items-center gap-2.5">
            <ShieldCheck className="w-4 h-4 text-primary-600 dark:text-primary-400 shrink-0" />
            <div>
              <span className="text-xs font-bold text-slate-900 dark:text-white block">
                Guia de Validação Criptográfica (HMAC-SHA256)
              </span>
              <span className="text-[11px] text-slate-500 dark:text-slate-400 block mt-0.5">
                Valide o cabeçalho <code className="font-mono text-[10px] bg-slate-200 dark:bg-slate-700 px-1 py-0.5 rounded">X-Webhook-Signature</code> no seu servidor para garantir integridade.
              </span>
            </div>
          </div>
          {showVerifyGuide ? (
            <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
          ) : (
            <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
          )}
        </button>

        {showVerifyGuide && (
          <div className="p-4 pt-0 border-t border-slate-200 dark:border-slate-700 space-y-3 mt-3 animate-fade-in">
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500 font-medium">Linguagem:</span>
              <div className="inline-flex p-0.5 rounded-lg bg-slate-200/70 dark:bg-slate-700 text-xs font-semibold">
                {(['node', 'python', 'php'] as const).map((lang) => (
                  <button
                    key={lang}
                    type="button"
                    onClick={() => setGuideLang(lang)}
                    className={`px-3 py-1 rounded-md text-xs transition-all ${
                      guideLang === lang
                        ? 'bg-white dark:bg-slate-800 text-primary-600 dark:text-primary-400 shadow-2xs font-bold'
                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                    }`}
                  >
                    {lang === 'node' ? 'Node.js' : lang === 'python' ? 'Python' : 'PHP'}
                  </button>
                ))}
              </div>
            </div>

            <div className="relative rounded-xl bg-slate-950 p-3.5 text-xs font-mono text-emerald-400 overflow-x-auto shadow-inner">
              <pre className="m-0 leading-relaxed">
                {guideLang === 'node' && `const crypto = require('crypto');

app.post('/webhook', express.raw({ type: 'application/json' }), (req, res) => {
  const signature = req.headers['x-webhook-signature'];
  const expected = crypto
    .createHmac('sha256', process.env.WEBHOOK_SECRET)
    .update(req.body)
    .digest('hex');

  if (signature !== expected) {
    return res.status(401).send('Assinatura inválida');
  }

  const payload = JSON.parse(req.body.toString());
  console.log('Evento validado com sucesso:', payload.event_type);
  res.status(200).send('OK');
});`}
                {guideLang === 'python' && `import hmac
import hashlib

def verify_webhook(raw_payload: bytes, signature_header: str, secret: str) -> bool:
    expected = hmac.new(secret.encode(), raw_payload, hashlib.sha256).hexdigest()
    return hmac.compare_digest(signature_header, expected)

# No endpoint FastAPI / Flask:
# if not verify_webhook(await request.body(), request.headers.get('x-webhook-signature'), SECRET):
#     raise HTTPException(status_code=401, detail="Assinatura inválida")`}
                {guideLang === 'php' && `<?php
$payload = file_get_contents('php://input');
$signature = $_SERVER['HTTP_X_WEBHOOK_SIGNATURE'] ?? '';
$secret = getenv('WEBHOOK_SECRET');

$expected = hash_hmac('sha256', $payload, $secret);

if (!hash_equals($expected, $signature)) {
    http_response_code(401);
    exit('Assinatura inválida');
}

$event = json_decode($payload, true);
// Processar evento com segurança...`}
              </pre>
            </div>
          </div>
        )}
      </div>

      {isAdding && (
        <form
          onSubmit={handleAddSubmit}
          className="bg-slate-50 dark:bg-slate-800/60 p-5 rounded-2xl border border-slate-200 dark:border-slate-700 space-y-4"
        >
          <div className="flex flex-col sm:flex-row sm:items-end gap-4">
            <div className="flex-1">
              <label htmlFor="webhook_url" className="label-modern">
                URL do Endpoint (HTTPS)
              </label>
              <input
                type="url"
                id="webhook_url"
                required
                value={newUrl}
                onChange={(e) => setNewUrl(e.target.value)}
                placeholder="https://seu-servidor.com/webhooks"
                className="input-modern text-sm py-2"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setIsAdding(false)}
                className="btn-base btn-sm btn-secondary px-4 py-2"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={createMutation.isPending}
                className="btn-base btn-sm btn-primary px-4 py-2"
              >
                {createMutation.isPending ? 'A guardar...' : 'Guardar'}
              </button>
            </div>
          </div>

          {/* Events Selection Checklist */}
          <div className="pt-2 border-t border-slate-200/80 dark:border-slate-700/80">
            <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-2">
              Eventos a Subscrever
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {AVAILABLE_EVENTS.map((ev) => {
                const isChecked = selectedEvents.includes(ev.id);
                return (
                  <label
                    key={ev.id}
                    className={`flex items-start gap-2.5 p-2.5 rounded-xl border text-xs cursor-pointer transition-all ${
                      isChecked
                        ? 'border-primary-500 bg-primary-50/60 dark:bg-primary-950/40 text-primary-900 dark:text-primary-200 ring-1 ring-primary-500/20'
                        : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:border-slate-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggleEvent(ev.id)}
                      className="mt-0.5 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                    />
                    <div className="min-w-0">
                      <span className="font-semibold block">{ev.name}</span>
                      <span className="text-[11px] text-slate-500 dark:text-slate-400 block mt-0.5 leading-snug">
                        {ev.desc}
                      </span>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {createMutation.isError && (
            <p className="mt-2 text-xs text-rose-600 dark:text-rose-400">
              {getErrorMessage(createMutation.error)}
            </p>
          )}
        </form>
      )}

      {webhooks.length === 0 && !isAdding ? (
        <div className="text-center py-6 bg-slate-50 dark:bg-slate-800/40 rounded-xl border border-slate-200 dark:border-slate-700 border-dashed">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Nenhum webhook configurado para esta aplicação.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {webhooks.map((webhook) => (
            <div
              key={webhook.id}
              className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs space-y-4"
            >
              <div className="flex flex-col sm:flex-row justify-between items-start gap-3">
                <div className="min-w-0">
                  <h4
                    className="font-mono font-semibold text-sm text-slate-900 dark:text-white truncate max-w-lg"
                    title={webhook.webhook_url}
                  >
                    {webhook.webhook_url}
                  </h4>
                  <div className="flex flex-wrap items-center gap-2 mt-1.5">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                        webhook.enabled
                          ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400'
                          : 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-400'
                      }`}
                    >
                      {webhook.enabled ? 'Ativo' : 'Pausado'}
                    </span>
                    {webhook.last_delivery_status && (
                      <span className="text-xs text-slate-500 dark:text-slate-400">
                        Última entrega: {new Date(webhook.last_delivery_at!).toLocaleString('pt-PT')} (
                        <span
                          className={
                            webhook.last_delivery_status === 'success'
                              ? 'text-emerald-600 dark:text-emerald-400 font-semibold'
                              : 'text-rose-600 dark:text-rose-400 font-semibold'
                          }
                        >
                          {webhook.last_delivery_status}
                        </span>
                        )
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      toggleMutation.mutate({ id: webhook.id, enabled: !webhook.enabled })
                    }
                    disabled={toggleMutation.isPending}
                    className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 inline-flex items-center gap-1.5 transition-colors"
                    title={webhook.enabled ? 'Pausar entrega' : 'Reativar entrega'}
                  >
                    {webhook.enabled ? (
                      <>
                        <PauseCircle className="w-3.5 h-3.5 text-amber-500" /> Pausar
                      </>
                    ) : (
                      <>
                        <PlayCircle className="w-3.5 h-3.5 text-emerald-500" /> Ativar
                      </>
                    )}
                  </button>

                  <button
                    onClick={() => testMutation.mutate(webhook.id)}
                    disabled={testMutation.isPending}
                    className="text-xs font-semibold px-3 py-1.5 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 inline-flex items-center gap-1 transition-colors"
                  >
                    <Send className="w-3.5 h-3.5" /> Testar Ping
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setOpenDeliveries((prev) => {
                        const next = new Set(prev);
                        if (next.has(webhook.id)) next.delete(webhook.id);
                        else next.add(webhook.id);
                        return next;
                      });
                    }}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors inline-flex items-center gap-1.5 ${
                      openDeliveries.has(webhook.id)
                        ? 'bg-indigo-50 border-indigo-200 text-indigo-700 dark:bg-indigo-950/40 dark:border-indigo-800 dark:text-indigo-300'
                        : 'border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                    }`}
                    title="Ver histórico de auditoria e entregas"
                  >
                    <History className="w-3.5 h-3.5 text-indigo-500" />
                    <span>Auditoria & Replay</span>
                    {openDeliveries.has(webhook.id) ? (
                      <ChevronDown className="w-3.5 h-3.5" />
                    ) : (
                      <ChevronRight className="w-3.5 h-3.5" />
                    )}
                  </button>
                  <button
                    onClick={async () => {
                      const res = await alert.confirm(
                        'Eliminar Webhook?',
                        'Tem a certeza que deseja eliminar este endpoint de webhook?'
                      );
                      if (res.isConfirmed) {
                        deleteMutation.mutate(webhook.id);
                      }
                    }}
                    className="text-xs font-semibold px-3 py-1.5 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/30 rounded-lg inline-flex items-center gap-1 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Eliminar
                  </button>
                </div>
              </div>

              {/* Subscribed Events Badges */}
              {webhook.events && webhook.events.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5 pt-1">
                  <span className="text-[11px] font-medium text-slate-400 dark:text-slate-500 mr-1">
                    Eventos:
                  </span>
                  {webhook.events.map((ev) => (
                    <span
                      key={ev}
                      className="px-2 py-0.5 rounded-md text-[10px] font-mono font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200/60 dark:border-slate-700/60"
                    >
                      {ev}
                    </span>
                  ))}
                </div>
              )}

              {/* Secret Key Area */}
              {webhook.secret_key && (
                <div className="pt-3 border-t border-slate-100 dark:border-slate-800">
                  <div className="flex justify-between items-center mb-1.5">
                    <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                      Chave Secreta HMAC (X-Webhook-Signature)
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleCopySecret(webhook.secret_key!, webhook.id)}
                        className="text-xs font-semibold text-slate-500 hover:text-primary-600 dark:text-slate-400 dark:hover:text-primary-400 inline-flex items-center gap-1"
                        title="Copiar chave secreta"
                      >
                        {copiedSecretId === webhook.id ? (
                          <>
                            <Check className="w-3.5 h-3.5 text-emerald-500" /> Copiado!
                          </>
                        ) : (
                          <>
                            <Copy className="w-3.5 h-3.5" /> Copiar
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => toggleSecretVisibility(webhook.id)}
                        className="text-xs font-semibold text-primary-600 dark:text-primary-400 hover:underline inline-flex items-center gap-1 ml-2"
                      >
                        {visibleSecrets.has(webhook.id) ? (
                          <>
                            <EyeOff className="w-3.5 h-3.5" /> Ocultar
                          </>
                        ) : (
                          <>
                            <Eye className="w-3.5 h-3.5" /> Mostrar
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="font-mono text-xs bg-slate-100 dark:bg-slate-800 p-2.5 rounded-lg text-slate-800 dark:text-slate-200 break-all border border-slate-200 dark:border-slate-700">
                    {visibleSecrets.has(webhook.id)
                      ? webhook.secret_key
                      : '••••••••••••••••••••••••••••••••'}
                  </div>
                </div>
              )}

              {/* Delivery History Section */}
              {openDeliveries.has(webhook.id) && (
                <div className="pt-4 border-t border-slate-200 dark:border-slate-800">
                  <WebhookDeliveryHistory
                    webhookId={webhook.id}
                    clientId={clientId}
                    applicationId={applicationId}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

interface WebhookDeliveryHistoryProps {
  webhookId: string;
  clientId: string;
  applicationId: string;
}

export const WebhookDeliveryHistory: React.FC<WebhookDeliveryHistoryProps> = ({
  webhookId,
  clientId,
  applicationId,
}) => {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<'all' | 'success' | 'failed'>('all');
  const [inspectedDelivery, setInspectedDelivery] = useState<WebhookDelivery | null>(null);
  const [copiedPayload, setCopiedPayload] = useState(false);

  const {
    data: deliveries = [],
    isLoading,
    refetch,
    isRefetching,
  } = useQuery<WebhookDelivery[]>({
    queryKey: ['webhook-deliveries', clientId, applicationId, webhookId],
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: WebhookDelivery[] }>(
        `/clients/${clientId}/applications/${applicationId}/webhooks/${webhookId}/deliveries`
      );
      return response.data?.data || [];
    },
  });

  const replayMutation = useMutation({
    mutationFn: async (deliveryId: string) => {
      const response = await api.post(
        `/clients/${clientId}/applications/${applicationId}/webhooks/${webhookId}/deliveries/${deliveryId}/replay`
      );
      return response.data;
    },
    onSuccess: () => {
      toast.success('Evento reenviado com sucesso! A aguardar confirmação de entrega.');
      void queryClient.invalidateQueries({
        queryKey: ['webhook-deliveries', clientId, applicationId, webhookId],
      });
      void refetch();
    },
    onError: (err) => {
      toast.error(`Falha ao reenviar evento: ${getErrorMessage(err)}`);
    },
  });

  const filteredDeliveries = deliveries.filter((d) => {
    if (statusFilter === 'all') return true;
    return d.status === statusFilter;
  });

  const handleCopyPayload = (payload: Record<string, unknown>) => {
    navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    setCopiedPayload(true);
    toast.success('Payload copiado para a área de transferência');
    setTimeout(() => setCopiedPayload(false), 2000);
  };

  return (
    <div className="space-y-4 bg-slate-50 dark:bg-slate-950/50 p-4 rounded-xl border border-slate-200/80 dark:border-slate-800">
      {/* Header with filters and actions */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-2 border-b border-slate-200 dark:border-slate-800">
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 text-indigo-500" />
          <h5 className="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200">
            Registo de Entregas & Tentativas
          </h5>
          <span className="text-xs text-slate-400 font-mono">({deliveries.length} eventos)</span>
        </div>

        <div className="flex items-center gap-2">
          {/* Status Filter Buttons */}
          <div className="inline-flex rounded-lg border border-slate-200 dark:border-slate-700 p-0.5 bg-white dark:bg-slate-900 text-[11px]">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-2.5 py-1 font-semibold rounded-md transition-colors ${
                statusFilter === 'all'
                  ? 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white'
                  : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-300'
              }`}
            >
              Todos
            </button>
            <button
              onClick={() => setStatusFilter('success')}
              className={`px-2.5 py-1 font-semibold rounded-md transition-colors ${
                statusFilter === 'success'
                  ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300'
                  : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-300'
              }`}
            >
              Sucesso
            </button>
            <button
              onClick={() => setStatusFilter('failed')}
              className={`px-2.5 py-1 font-semibold rounded-md transition-colors ${
                statusFilter === 'failed'
                  ? 'bg-rose-50 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300'
                  : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-300'
              }`}
            >
              Falhas
            </button>
          </div>

          <button
            onClick={() => refetch()}
            disabled={isRefetching}
            className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 bg-white dark:bg-slate-900"
            title="Atualizar histórico de entregas"
          >
            <RotateCw className={`w-3.5 h-3.5 ${isRefetching ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Deliveries List */}
      {isLoading ? (
        <div className="py-6 text-center text-xs text-slate-500">
          <RotateCw className="w-4 h-4 animate-spin mx-auto mb-1 text-primary-500" />
          <span>A carregar entregas...</span>
        </div>
      ) : filteredDeliveries.length === 0 ? (
        <div className="py-6 text-center text-xs text-slate-500 italic">
          Nenhuma entrega encontrada para este filtro. Execute &quot;Testar Ping&quot; para disparar um evento simulado.
        </div>
      ) : (
        <div className="divide-y divide-slate-200/80 dark:divide-slate-800/80 max-h-80 overflow-y-auto">
          {filteredDeliveries.map((delivery) => {
            const isSuccess = delivery.status === 'success';
            const isFailed = delivery.status === 'failed';

            return (
              <div
                key={delivery.id}
                className="py-2.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs"
              >
                <div className="flex items-start sm:items-center gap-2.5 min-w-0">
                  {isSuccess ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5 sm:mt-0" />
                  ) : isFailed ? (
                    <XCircle className="w-4 h-4 text-rose-500 shrink-0 mt-0.5 sm:mt-0" />
                  ) : (
                    <Clock className="w-4 h-4 text-amber-500 shrink-0 mt-0.5 sm:mt-0" />
                  )}

                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono font-bold text-slate-900 dark:text-white">
                        {delivery.event_type}
                      </span>
                      <span
                        className={`px-1.5 py-0.5 rounded text-[10px] font-mono font-bold ${
                          isSuccess
                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                            : isFailed
                            ? 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300'
                            : 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300'
                        }`}
                      >
                        {delivery.response_status ? `HTTP ${delivery.response_status}` : delivery.status.toUpperCase()}
                      </span>
                      <span className="text-[11px] text-slate-400">
                        {delivery.attempts} tentativa(s)
                      </span>
                    </div>

                    <div className="flex items-center gap-3 text-[11px] text-slate-400 mt-0.5">
                      <span>{new Date(delivery.created_at).toLocaleString('pt-PT')}</span>
                      {delivery.error_message && (
                        <span className="text-rose-500 truncate max-w-xs" title={delivery.error_message}>
                          {delivery.error_message}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1.5 shrink-0 self-end sm:self-auto">
                  <button
                    onClick={() => setInspectedDelivery(delivery)}
                    className="px-2.5 py-1 rounded bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 text-xs font-semibold inline-flex items-center gap-1"
                    title="Inspecionar payload e resposta"
                  >
                    <Code className="w-3.5 h-3.5 text-primary-500" />
                    <span>Inspecionar</span>
                  </button>

                  <button
                    onClick={() => replayMutation.mutate(delivery.id)}
                    disabled={replayMutation.isPending}
                    className="px-2.5 py-1 rounded bg-indigo-50 dark:bg-indigo-950/50 border border-indigo-200 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 text-xs font-semibold inline-flex items-center gap-1"
                    title="Reenviar este evento para o endpoint"
                  >
                    <RotateCw
                      className={`w-3.5 h-3.5 ${
                        replayMutation.isPending && replayMutation.variables === delivery.id
                          ? 'animate-spin'
                          : ''
                      }`}
                    />
                    <span>Reenviar</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Payload & Delivery Inspector Modal */}
      {inspectedDelivery && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl max-w-2xl w-full p-6 shadow-2xl my-8 max-h-[85vh] flex flex-col">
            {/* Header */}
            <div className="flex items-start justify-between pb-3 border-b border-slate-200 dark:border-slate-800">
              <div>
                <h4 className="text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
                  <History className="w-4 h-4 text-indigo-500" />
                  <span>Inspeção da Entrega de Webhook</span>
                </h4>
                <p className="text-xs font-mono text-slate-400 mt-0.5">
                  ID: {inspectedDelivery.id}
                </p>
              </div>
              <button
                onClick={() => setInspectedDelivery(null)}
                className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto py-4 space-y-4 text-xs">
              {/* Meta Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="p-2.5 rounded-lg bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
                  <span className="text-[10px] text-slate-400 uppercase font-bold block">Evento</span>
                  <span className="font-mono font-semibold text-slate-900 dark:text-white">
                    {inspectedDelivery.event_type}
                  </span>
                </div>
                <div className="p-2.5 rounded-lg bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
                  <span className="text-[10px] text-slate-400 uppercase font-bold block">Estado</span>
                  <span
                    className={`font-semibold ${
                      inspectedDelivery.status === 'success'
                        ? 'text-emerald-600'
                        : 'text-rose-600'
                    }`}
                  >
                    {inspectedDelivery.status.toUpperCase()}
                  </span>
                </div>
                <div className="p-2.5 rounded-lg bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
                  <span className="text-[10px] text-slate-400 uppercase font-bold block">Código HTTP</span>
                  <span className="font-mono font-semibold text-slate-900 dark:text-white">
                    {inspectedDelivery.response_status || 'N/A'}
                  </span>
                </div>
                <div className="p-2.5 rounded-lg bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
                  <span className="text-[10px] text-slate-400 uppercase font-bold block">Tentativas</span>
                  <span className="font-semibold text-slate-900 dark:text-white">
                    {inspectedDelivery.attempts}
                  </span>
                </div>
              </div>

              {/* Error Message if any */}
              {inspectedDelivery.error_message && (
                <div className="p-3 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900 rounded-lg text-rose-800 dark:text-rose-300">
                  <strong>Erro reportado:</strong> {inspectedDelivery.error_message}
                </div>
              )}

              {/* Payload Section */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-slate-700 dark:text-slate-300">
                    Payload Transmitido (JSON)
                  </span>
                  <button
                    onClick={() => handleCopyPayload(inspectedDelivery.payload)}
                    className="text-xs text-primary-600 dark:text-primary-400 hover:underline inline-flex items-center gap-1"
                  >
                    {copiedPayload ? (
                      <>
                        <Check className="w-3.5 h-3.5 text-emerald-500" /> Copiado!
                      </>
                    ) : (
                      <>
                        <Copy className="w-3.5 h-3.5" /> Copiar JSON
                      </>
                    )}
                  </button>
                </div>
                <pre className="p-3 bg-slate-100 dark:bg-slate-800/80 text-slate-800 dark:text-slate-200 rounded-lg font-mono text-xs overflow-x-auto max-h-48 border border-slate-200 dark:border-slate-700">
                  {JSON.stringify(inspectedDelivery.payload, null, 2)}
                </pre>
              </div>

              {/* Response Body if available */}
              {inspectedDelivery.response_body && (
                <div className="space-y-1.5">
                  <span className="font-bold text-slate-700 dark:text-slate-300">
                    Resposta Recebida do Servidor de Destino
                  </span>
                  <pre className="p-3 bg-slate-100 dark:bg-slate-800/80 text-slate-800 dark:text-slate-200 rounded-lg font-mono text-xs overflow-x-auto max-h-32 border border-slate-200 dark:border-slate-700">
                    {inspectedDelivery.response_body}
                  </pre>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="pt-3 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between">
              <button onClick={() => setInspectedDelivery(null)} className="btn-base btn-secondary btn-sm">
                Fechar
              </button>
              <button
                onClick={() => {
                  replayMutation.mutate(inspectedDelivery.id);
                  setInspectedDelivery(null);
                }}
                disabled={replayMutation.isPending}
                className="btn-base btn-primary btn-sm inline-flex items-center gap-1.5"
              >
                <RotateCw className="w-3.5 h-3.5" />
                <span>Reenviar Agora</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
